import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Button, ButtonToolbar } from 'reactstrap';
import api from '@/utils/apiClient';
import { emptyStringPattern } from '@/shared/helpers';
import { FormField } from '@/shared/components/form/hook/FormField';
import { updateSubCategoryInList } from '../../redux/actions';
import LoadingIcon from 'mdi-react/LoadingIcon';

const SubcategoryEditForm = ({ modal, isEdit, id }) => {
  const dispatch = useDispatch();

  //states
  const [state, setState] = React.useState({
    loading: false,
    fetchingSubCategory: false,
    newSubCategoryAdded: false,
    errorMessage: '',
  });

  // prefill the form in case of edit subCategory
  React.useEffect(() => {
    if (isEdit && id) {
      setDefaultData(+id);
    }
  }, [isEdit, id]);

  const setDefaultData = async (subCategoryId) => {
    try {
      const { data: res, isSuccess } = await api.subCategory.getSubCategoryById(subCategoryId);
      if (isSuccess) {
        ['id', 'name', 'description'].forEach((field) => setValue(field, res?.data?.[field]));
      } else {
        throw new Error(res?.messages?.[0]);
      }
    } catch ({ message }) {
      console.error('SubCategory edit error : ', message);
      setState({ ...state, errorMessage: 'Error while getting existing subCategory details' });
    }
  };

  // subCategory form related functions
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const resetForm = () => {
    reset({ id: 0, name: '', description: '' });
  };

  const onSubmit = async ({ name, description }) => {
    setState({ ...state, loading: true });
    const defaultError = `Error occurred while ${isEdit ? 'Updating' : 'Adding new'} subcategory`;
    try {
      const { data, isSuccess } = isEdit
        ? await api.subCategory.editSubCategory({ id, name, description })
        : await api.subCategory.addSubCategory({ name, description });
      if (isSuccess) {
        setState({ ...state, newSubCategoryAdded: true, loading: false });

        if (isEdit) dispatch(updateSubCategoryInList(data));
        else resetForm();
      } else {
        throw new Error(data?.messages?.[0] ?? defaultError);
      }
    } catch (error) {
      console.error(error);
      setState({
        ...state,
        loading: false,
        newSubCategoryAdded: false,
        errorMessage: error.message,
      });
    }
  };

  const onCancel = () => {
    resetForm();
    modal.hide();
  };

  return (
    <div>
      {/*Add/Edit SubCategory Form Status*/}
      <div className="ml-4">
        {state.loading && (
          <div>
            <div className="panel__refresh position-relative" style={{ height: 75, width: 20 }}>
              <LoadingIcon />
              <div style={{ width: 200, padding: '23px 40px' }}>
                {isEdit ? 'Updating' : 'Adding new'} subcategory...
              </div>
            </div>
          </div>
        )}
        {(state.newSubCategoryAdded || !!state.errorMessage) && (
          <table className="mt-3">
            <tr>
              <td>
                {state.newSubCategoryAdded ? (
                  <span className="lnr lnr-thumbs-up modal__title-icon success" />
                ) : (
                  <span className="lnr lnr-cross-circle modal__title-icon error" />
                )}
              </td>
              <td className="pl-2">
                {state.newSubCategoryAdded
                  ? `${isEdit ? 'Subcategory Updated' : 'New Subcategory Added'} successfully`
                  : state.errorMessage}
              </td>
            </tr>
          </table>
        )}
      </div>

      {/*Add/Edit SubCategory Form*/}
      <div className="modal__body">
        <form className={`form`} onSubmit={handleSubmit(onSubmit)}>
          <div className="form__form-group">
            <span className="form__form-group-label">Name</span>
            <div className="form__form-group-field">
              <FormField
                name="name"
                control={control}
                component="input"
                errors={errors}
                rules={{
                  required: 'This is required field',
                  pattern: emptyStringPattern,
                }}
                defaultValue=""
                isAboveError={true}
                placeholder="Name of the subcategory"
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">Description</span>
            <div className="form__form-group-field">
              <FormField
                name="description"
                control={control}
                component="textarea"
                errors={errors}
                rules={{
                  required: 'This is required field',
                  pattern: emptyStringPattern,
                }}
                defaultValue=""
                isAboveError={true}
                placeholder="Write description about subcategory"
              />
            </div>
          </div>
          <ButtonToolbar className="form__button-toolbar modal__footer">
            <Button disabled={state.loading} color="primary" type="submit">
              Submit
            </Button>
            <Button type="button" className="modal_cancel" onClick={onCancel}>
              Close
            </Button>
          </ButtonToolbar>
        </form>
      </div>
    </div>
  );
};

export default SubcategoryEditForm;
